const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";

class ApiClient {
  private authToken: string | null = null;

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private async request<T>(path: string, options: RequestInit = {}, json = true): Promise<T> {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    if (json) {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
    }

    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: "Request failed" } }));
      throw new Error(error.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path);
  }

  async post<T>(path: string, body?: any): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async postForm<T>(path: string, body: FormData): Promise<T> {
    return this.request<T>(
      path,
      {
        method: "POST",
        body,
      },
      false
    );
  }

  async patch<T>(path: string, body: any): Promise<T> {
    return this.request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "DELETE" });
  }
}

export const api = new ApiClient();

// API types
export type TaxStatus = "calculated" | "needs_review" | "unavailable";

export interface TaxComponent {
  type: string;
  label: string;
  rate: number | null;
  amount: number;
  source: string;
  description: string | null;
}

export interface TransactionTaxItem {
  id: string;
  item_label: string;
  tax_category: string | null;
  hsn_sac: string | null;
  tax_basis: "inclusive" | "exclusive";
  taxable_amount: number;
  gross_amount: number;
  total_tax_amount: number;
  gst_amount: number;
  cess_amount: number;
  vat_amount: number;
  excise_amount: number;
  stamp_duty_amount: number;
  tcs_amount: number;
  tds_amount: number;
  local_tax_amount: number;
  other_tax_amount: number;
  total_tax_rate: number | null;
  gst_rate: number | null;
  cess_rate: number | null;
  tax_components: TaxComponent[];
  rate_source_title: string | null;
  rate_source_url: string | null;
  confidence: number;
  status: TaxStatus;
  review_reason: string | null;
  calculation_source: string;
}

export interface TransactionTaxSummary {
  total_tax_paid: number;
  gst_total: number;
  cess_total: number;
  other_tax_total: number;
  status: TaxStatus | "not_applicable";
  items_needing_review: number;
}

export interface Transaction {
  id: string;
  source: string;
  raw_text: string | null;
  amount: number;
  currency: string;
  merchant: string | null;
  category: string | null;
  subcategory: string | null;
  payment_mode: string | null;
  txn_type: string;
  account_last4: string | null;
  timestamp: string;
  confidence: number | null;
  parse_status: string;
  parse_signals: any[];
  ai_model: string | null;
  user_notes: string | null;
  created_at: string;
  tax_items?: TransactionTaxItem[];
  tax_summary?: TransactionTaxSummary;
  nutrition_items?: NutritionItem[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface MonthlySummary {
  month: string;
  total_expense: number;
  total_income: number;
  net: number;
  transaction_count: number;
  categories: Array<{
    category: string;
    total: number;
    count: number;
    percentage: number;
    trend: string;
  }>;
  top_merchants: Array<{
    merchant: string;
    total: number;
    count: number;
  }>;
  payment_modes?: Array<{
    payment_mode: string;
    total: number;
    count: number;
  }>;
  tax_summary: {
    gst_total: number;
    cess_total: number;
    other_tax_total: number;
    total_tax_paid: number;
    taxable_spend: number;
    items_needing_review: number;
    by_tax_type: Array<{ tax_type: string; total: number; count: number }>;
    by_category: Array<{
      category: string;
      total_tax_paid: number;
      gst_total: number;
      cess_total: number;
      other_tax_total: number;
      count: number;
    }>;
  };
}

export interface Forecast {
  id: string;
  period_type: string;
  period_start: string;
  period_end: string;
  total_projected: number;
  confidence_low: number;
  confidence_high: number;
  category_projections: Array<{
    category: string;
    projected_amount: number;
    trend: string;
  }>;
  top_drivers: string[];
  assumptions: string[];
}

export interface AgentJob {
  id: string;
  agent_type: string;
  status: string;
  attempts: number;
  created_at: string;
  last_error: string | null;
}

export interface AuthUser {
  id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
}

export interface AuthSessionResponse {
  user_id: string;
  token: string;
  token_type: "Bearer";
  expires_at: string;
  user: AuthUser;
}

export interface AuthMeResponse {
  user: AuthUser;
}

// Nutrition types
export type HealthCategory =
  | "protein_rich" | "carb_heavy" | "high_sugar" | "high_fat" | "balanced"
  | "fruits_vegetables" | "alcohol" | "tobacco" | "junk_food" | "unknown";

export interface NutritionItem {
  id: string;
  item_name: string;
  meal_type: string | null;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  fiber_g: number | null;
  sugar_g: number | null;
  health_category: HealthCategory;
  is_healthy: boolean | null;
  alcohol_content: boolean;
  tobacco_content: boolean;
}

export interface ItemizationRequest {
  id: string;
  transaction_id: string;
  status: string;
  request_reason: string;
  merchant_hint: string | null;
}

export interface MonthlyNutritionSummary {
  month: string;
  total_food_transactions: number;
  total_items: number;
  healthy_count: number;
  unhealthy_count: number;
  health_score: number;
  macros_avg: { protein_g: number; carbs_g: number; fat_g: number; fiber_g: number; sugar_g: number };
  total_calories: number;
  health_category_breakdown: Array<{ category: string; count: number; percentage: number }>;
  alcohol_count: number;
  tobacco_count: number;
  top_food_items: Array<{ item_name: string; count: number }>;
  meal_type_distribution: Array<{ meal_type: string; count: number }>;
  insight: NutritionInsight | null;
}

export interface NutritionInsight {
  id: string;
  health_score: number;
  direction: string;
  summary: string;
  strengths: string[];
  concerns: string[];
  tips: string[];
  created_at: string;
}

export interface NutritionTrend {
  months: Array<{ month: string; health_score: number; healthy_count: number; unhealthy_count: number }>;
  direction: string;
}
